import { Link } from 'react-router-dom';
import { Users, Target, TrendingUp, BookOpen } from 'lucide-react';

export function DemoLandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Find My Way</h1>
            <div className="flex gap-4">
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Try Find My Way - No Signup Required
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore how Find My Way helps you live according to your values.
            See real examples of how others use the platform to make better decisions.
          </p>
        </div>

        {/* Demo Profile Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                      <Users className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Alex Chen</h3>
                      <p className="text-indigo-100">Product Manager, 30 years old</p>
                    </div>
                  </div>
                  <p className="text-white text-lg mt-4 max-w-2xl">
                    Follow Alex's 3-month journey balancing career ambition with family time,
                    maintaining health habits, and staying true to core values.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-indigo-600">11</div>
                  <div className="text-sm text-gray-600 mt-1">Core Values</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">3</div>
                  <div className="text-sm text-gray-600 mt-1">Active Goals</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-pink-600">7</div>
                  <div className="text-sm text-gray-600 mt-1">Daily Habits</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">10</div>
                  <div className="text-sm text-gray-600 mt-1">Journal Entries</div>
                </div>
              </div>

              {/* Features */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="flex gap-3">
                  <Target className="w-6 h-6 text-indigo-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Values-Aligned Goals</h4>
                    <p className="text-sm text-gray-600">
                      See how Alex links goals to core values for meaningful progress
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <TrendingUp className="w-6 h-6 text-purple-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Habit Tracking</h4>
                    <p className="text-sm text-gray-600">
                      Explore daily routines that support work-life balance
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <BookOpen className="w-6 h-6 text-pink-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Reflection Journal</h4>
                    <p className="text-sm text-gray-600">
                      Read authentic reflections on challenges and wins
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/demo/demo-30yo-professional"
                  className="px-8 py-3 text-lg font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 text-center shadow-md hover:shadow-lg transition-all"
                >
                  Explore Alex's Profile →
                </Link>
                <Link
                  to="/signup"
                  className="px-8 py-3 text-lg font-semibold text-indigo-600 bg-white border-2 border-indigo-600 rounded-lg hover:bg-indigo-50 text-center transition-all"
                >
                  Create Your Own Profile
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Coming Soon */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">More demo profiles coming soon</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
            <span className="px-4 py-2 bg-white rounded-full border border-gray-200">
              🎓 Recent Graduate
            </span>
            <span className="px-4 py-2 bg-white rounded-full border border-gray-200">
              👔 Mid-Career Professional
            </span>
            <span className="px-4 py-2 bg-white rounded-full border border-gray-200">
              🏠 Parent & Entrepreneur
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">
            © 2026 Find My Way. All demo data is fictional for illustration purposes.
          </p>
        </div>
      </footer>
    </div>
  );
}

